import { haversineMiles } from './googleMaps.js'

/**
 * directoryOffice — s106 (TestFlight b72): two small rules for a directory doctor (not on Orella).
 *
 * withOriginMiles: "0 mi" on every row (his screenshot). The API measures ZIP centre to ZIP centre,
 *   so every office in the patient's ZIP read 0. A row carries its office's street-address point
 *   (lat / lng, s105 geocode); with the patient's point (device, searched address, home ZIP centre or
 *   the searched area) the distance is worked out ON THE PHONE and the rows are re-sorted by it. A row
 *   without a point keeps the API's figure. PURE.
 *
 * officeSpecialtyName: the Doctors-tab specialty name for a directory row's taxonomy description
 *   ("Dentist, General Practice" -> 'Dental'), so StartCaseSheet can offer the case areas that
 *   specialty covers. Primary care and anything unknown answer null: every area. PURE.
 */
export function withOriginMiles(rows, origin) {
  if (!Array.isArray(rows)) return []
  if (!origin || !Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) return rows
  const out = rows.map((r) => {
    if (r && typeof r.lat === 'number' && typeof r.lng === 'number' && Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
      return { ...r, miles: Math.round(haversineMiles(origin.lat, origin.lng, r.lat, r.lng) * 10) / 10 }
    }
    return r
  })
  return out.slice().sort((a, b) => (typeof a.miles === 'number' ? a.miles : 1e9) - (typeof b.miles === 'number' ? b.miles : 1e9))
}

// NPPES descriptions read "<classification>, <specialization>". The specialization is tried first
// ("Internal Medicine, Cardiovascular Disease" is Cardiology; "Psychiatry & Neurology, Psychiatry" is
// Psychiatry), then the whole description; primary-care words only after both, so "Dentist, General
// Practice" is Dental and "Nurse Practitioner, Family" (no rule) is primary care.
const RULES = [
  [/\b(dentist|dental|orthodont|periodont|endodont|prosthodont|oral)\b/i, 'Dental'],
  [/\botolaryngolog/i, 'Otolaryngology (ENT)'], [/\bophthalmolog|\boptomet/i, 'Ophthalmology'],
  [/\ballergy|\bimmunolog/i, 'Allergy & Immunology'], [/\bendocrinolog/i, 'Endocrinology'],
  [/\borthop/i, 'Orthopedics'], [/\bphysical medicine|\brehabilitation/i, 'Physical Medicine & Rehab'],
  [/\brheumatolog/i, 'Rheumatology'], [/\bsports medicine/i, 'Sports Medicine'], [/\bpain/i, 'Pain Management'],
  [/\bdermatolog/i, 'Dermatology'], [/\bgastroenterolog/i, 'Gastroenterology'], [/\burolog/i, 'Urology'],
  [/\bcardio/i, 'Cardiology'], [/\bpulmon/i, 'Pulmonology'], [/\bnephrolog/i, 'Nephrology'],
  [/\bhematolog|\boncolog/i, 'Hematology'], [/\bobstetric|\bgynecolog|\bwomen/i, 'Obstetrics & Gynecology'],
  [/\bsleep/i, 'Sleep Medicine'], [/\bneurolog/i, 'Neurology'], [/\bpsychiatr|\bmental health|\bpsycholog/i, 'Psychiatry'],
  [/\bvascular/i, 'Vascular Surgery'], [/\binfectious/i, 'Infectious Disease'], [/\bpodiatr|\bfoot/i, 'Podiatry'],
  [/\bplastic/i, 'Plastic Surgery'],
]
export function officeSpecialtyName(desc) {
  const whole = String(desc || '')
  if (!whole) return null
  const spec = whole.includes(',') ? whole.slice(whole.lastIndexOf(',') + 1) : whole
  for (const part of [spec, whole]) for (const [re, name] of RULES) if (re.test(part)) return name
  return null // primary care (family, general practice, internal medicine...) and anything unknown: every area
}
